/**
 * subject-loaded.guard.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */


import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Resolve
} from "@angular/router";
import { Observable } from "rxjs/Rx";

import { AlertService } from "../common/alert.service";
import { SharedList, WatchlistService } from "../watchlist/watchlist.service";

/**
 * A conjunction type for routes with ID values in their parameters.
 */
interface ListIdPaired {
  params : {
    groupId : string,
    objectId : string
  };
}

/**
 * A {@link SharedWorklistGuard} guards routes that require a shared worklist
 */
@Injectable()
export class SharedWorklistGuard implements CanActivate, Resolve<SharedList>
{
  /**
   * Create the {@link SharedWorklistGuard}
   * @param  {WatchlistService} watchlist
   * @param  {AlertService}   alerts
   * @return {SharedWorklistGuard}
   */
  constructor (
    protected watchlist : WatchlistService,
    protected alerts : AlertService
  )
  {
    console.log("SharedWorklistGuard args", Array.from(arguments));
  }

  /**
   * Determine whether a route snapshot is {@link ListIdPaired}. Note the
   * return type is a boolean "type predicate" which can signal to TS about
   * the argument's type in subsequent code branches.
   * @param  {ActivatedRouteSnapshot} rt
   * @return {Boolean}
   */
  protected hasIdPair (rt : ActivatedRouteSnapshot)
    : rt is ActivatedRouteSnapshot & ListIdPaired
  {
    return ("groupId" in rt.params) && ("objectId" in rt.params);
  }

  public canActivate (route : ActivatedRouteSnapshot) : boolean
  {
    if (this.hasIdPair(route))
    {
      const lists = this.watchlist.sharedListsByGroup.get(
        +route.params.groupId);
      if (lists)
      {
        const found = (lists.find(
            (l : SharedList) => l.objectId === +route.params.objectId
          ) !== null);
        if (!found) {
          this.alerts.error(`Could not find worklist ${route.params.objectId} in the ${lists.length} lists for group`);
          return false;
        }
        return true;
      }
      else
      {
        this.alerts.error(`Could not find worklists for group ${route.params.groupId}`);
        return false;
      }
    }
    else
    {
      this.alerts.error(`Cannot open worklist ${route.params.groupId}:${route.params.objectId}`);
      return false;
    }
  }

  static RELOAD_AGE = 5 * 60 * 60 * 1000; // 5min

  public resolve (route : ActivatedRouteSnapshot) : Observable<SharedList> | SharedList
  {
    if (!this.hasIdPair(route))
    {
      // As long as we always apply this class as CanActivate AND Resolve, this
      // should never happen (i.e. it's a severe static misconfiguration).
      throw new Error("ID parameter missing in resolve guard.");
    }
    const groupLists = this.watchlist.sharedListsByGroup.get(
      +route.params.groupId);
    const possiblyLoaded = groupLists && groupLists.find(
      (l : SharedList) => l.objectId === +route.params.objectId);
    if (!possiblyLoaded) {
      throw new Error("Lists not present in worklist service (resolve guard).");
    }
    if (possiblyLoaded.loading === false 
      && possiblyLoaded.loadTime > Date.now() - SharedWorklistGuard.RELOAD_AGE)
    {
      return possiblyLoaded;
    } else {
      return this.watchlist.loadSharedList(
        +route.params.groupId,
        +route.params.objectId)
    }
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/routing/shared-worklist.guard.ts